import { appConfig } from '../config/env'
import { ApiError } from '../api/types'
import { currentPath, isSafeReturnPath, rememberReturnRoute } from './returnRoute'

export interface SessionUser {
  readonly issuer: string
  readonly sub: string
  readonly email: string | null
  readonly name: string | null
  readonly picture: string | null
  readonly emailVerified: boolean
}
export interface SessionRead {
  readonly user: SessionUser
  readonly accessTokenExpiresAt: number
}
export interface SessionState {
  readonly status: 'loading' | 'unauthenticated' | 'unverified' | 'authenticated' | 'error'
  readonly user: SessionUser | null
  readonly accessTokenExpiresAt: number | null
  readonly error: ApiError | null
}
interface LockAccess {
  request(name: string, run: () => Promise<unknown>): Promise<unknown>
}
interface SignalChannel {
  postMessage(value: unknown): void
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void
  close(): void
}
interface Dependencies {
  fetch: typeof fetch
  locks: LockAccess | null
  channel: SignalChannel | null
}
const initial: SessionState = {
  status: 'loading',
  user: null,
  accessTokenExpiresAt: null,
  error: null,
}
export const SESSION_LOCK = 'argus.browser-session'
export function refreshRequired(body: unknown): boolean {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return false
  const detail = body.detail
  return (
    typeof detail === 'object' &&
    detail !== null &&
    'code' in detail &&
    detail.code === 'session_refresh_required'
  )
}
function failure(status: number, message: string, retryable: boolean): ApiError {
  return new ApiError({
    kind: status === 401 ? 'Unauthenticated' : status === 0 ? 'Network' : 'Unexpected',
    status,
    message,
    retryable,
    problem: null,
    requestId: null,
  })
}

export class SessionClient {
  private state = initial
  private generation = 0
  private signedOut = false
  private listeners = new Set<() => void>()
  private pending: Promise<void> | null = null
  private logoutPending = false
  private baseUrl: string
  private dependencies: Dependencies
  constructor(baseUrl: string, dependencies: Dependencies) {
    this.baseUrl = baseUrl
    this.dependencies = dependencies
    dependencies.channel?.addEventListener('message', (event) => {
      if (event.data === 'logout') this.invalidate()
      if (event.data === 'refresh' && !this.signedOut) {
        void this.restore()
      }
    })
  }
  getSnapshot = (): SessionState => this.state
  getGeneration = (): number => this.generation
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
  private publish(state: SessionState): void {
    this.state = state
    this.listeners.forEach((listener) => listener())
  }
  private invalidate(): void {
    this.generation++
    this.signedOut = true
    this.publish({ ...initial, status: 'unauthenticated' })
  }
  private accept(read: SessionRead, generation: number): void {
    if (generation !== this.generation || this.signedOut) return
    if (
      this.state.user &&
      (this.state.user.issuer !== read.user.issuer || this.state.user.sub !== read.user.sub)
    )
      this.generation++
    this.publish({
      status: read.user.emailVerified ? 'authenticated' : 'unverified',
      user: read.user,
      accessTokenExpiresAt: read.accessTokenExpiresAt,
      error: null,
    })
  }
  private reject(error: unknown, generation: number): void {
    if (generation !== this.generation) return
    const typed =
      error instanceof ApiError
        ? error
        : failure(0, 'The request could not reach the Argus API.', true)
    if (typed.failure.status === 401) this.invalidate()
    else this.publish({ ...this.state, status: 'error', error: typed })
  }
  private supported(): void {
    if (!this.dependencies.locks)
      throw failure(
        0,
        'This browser requires Web Locks in a secure context. Use a supported browser over HTTPS.',
        false,
      )
  }
  private async call(path: string, body?: unknown): Promise<Response> {
    return this.dependencies.fetch(`${this.baseUrl}/auth/${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers:
        body === undefined
          ? { Accept: 'application/json' }
          : {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Argus-CSRF': '1',
            },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
  }
  private async read(response: Response): Promise<SessionRead> {
    if (!response.ok)
      throw failure(
        response.status,
        response.status === 401
          ? 'Your session has ended.'
          : 'The session could not be checked. Try again.',
        response.status === 429 || response.status >= 500,
      )
    return response.json() as Promise<SessionRead>
  }
  restore = async (): Promise<void> => {
    const generation = this.generation
    if (this.logoutPending) return
    this.signedOut = false
    try {
      this.supported()
      const response = await this.call('session')
      if (response.status === 401 && refreshRequired(await response.clone().json()))
        await this.renew(false)
      else this.accept(await this.read(response), generation)
    } catch (error) {
      this.reject(error, generation)
    }
  }
  renew = (force = false): Promise<void> => {
    if (this.pending) return this.pending
    const generation = this.generation
    const work = async () => {
      this.supported()
      await this.dependencies.locks!.request(SESSION_LOCK, async () => {
        if (generation !== this.generation || this.signedOut) return
        if (!force) {
          const response = await this.call('session')
          if (response.ok) {
            const read = await this.read(response)
            if (read.accessTokenExpiresAt > Date.now() / 1000 + 30) {
              this.accept(read, generation)
              return
            }
          } else if (
            response.status !== 401 ||
            !refreshRequired(await response.clone().json())
          ) {
            await this.read(response)
          }
        }
        if (generation !== this.generation || this.signedOut) return
        this.accept(await this.read(await this.call('refresh', {})), generation)
        if (generation === this.generation && !this.signedOut)
          this.dependencies.channel?.postMessage('refresh')
      })
    }
    this.pending = work()
      .catch((error: unknown) => {
        this.reject(error, generation)
        throw error
      })
      .finally(() => {
        this.pending = null
      })
    return this.pending
  }
  ensure = async (): Promise<void> => {
    if (this.signedOut || this.state.status === 'unauthenticated')
      throw failure(401, 'Your session has ended.', false)
    if (this.state.status === 'error')
      throw this.state.error ?? failure(0, 'The session could not be checked.', true)
    if (
      this.state.accessTokenExpiresAt !== null &&
      this.state.accessTokenExpiresAt <= Date.now() / 1000 + 30
    )
      await this.renew()
  }
  login = async (returnTo = currentPath()): Promise<void> => {
    const destination = isSafeReturnPath(returnTo) ? returnTo : '/'
    rememberReturnRoute(destination)
    try {
      this.supported()
      const response = await this.call('login', { return_to: destination })
      if (!response.ok) {
        await this.read(response)
        return
      }
      const result = (await response.json()) as { authorization_url: string }
      window.location.assign(result.authorization_url)
    } catch (error) {
      this.reject(error, this.generation)
    }
  }
  logout = async (): Promise<{
    logout_url: string
    revocation: 'revoked' | 'not_present' | 'failed'
  } | null> => {
    this.logoutPending = true
    this.invalidate()
    this.dependencies.channel?.postMessage('logout')
    const generation = this.generation
    try {
      this.supported()
      let result: {
        logout_url: string
        revocation: 'revoked' | 'not_present' | 'failed'
      } | null = null
      await this.dependencies.locks!.request(SESSION_LOCK, async () => {
        const response = await this.call('logout', {})
        if (!response.ok) {
          await this.read(response)
          return
        }
        result = (await response.json()) as typeof result
      })
      this.logoutPending = false
      return result
    } catch (error) {
      this.reject(error, generation)
      return null
    }
  }
  retry = async (): Promise<void> => {
    if (this.logoutPending) {
      const result = await this.logout()
      if (result?.revocation === 'failed')
        window.alert('You are signed out of Argus. Remote revocation was not confirmed.')
      if (result) window.location.assign(result.logout_url)
    } else await this.restore()
  }
}

let client: SessionClient | null = null
export function getSessionClient(
  baseUrl = appConfig.ok ? appConfig.config.apiBaseUrl : '',
): SessionClient {
  client ??= new SessionClient(baseUrl, {
    fetch: (...args) => fetch(...args),
    locks: typeof navigator !== 'undefined' ? (navigator.locks ?? null) : null,
    channel:
      typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel('argus.session')
        : null,
  })
  return client
}
